import type { AppRouteRecordRaw, Menu } from "/@/router/types";
import { defineStore } from "pinia";
import { store } from "/@/store";
import { flatMultiLevelRoutes } from "/@/router/helper/routeHelper";
import { transformRouteToMenu } from "/@/router/helper/menuHelper";
import { filter } from "/@/utils/helper/treeHelper";
import { useUserStore } from "./user";
import { asyncRoutes, PAGE_NOT_FOUND_ROUTE } from "/@/router";
import { ManagerType } from "@/router/constants";
import { AccountType } from "@/models/user";
import { router } from "/@/router";
import { RouteRecordRaw } from "vue-router";
import { useRegisterStore } from "./register";

interface PermissionState {
  // Whether the route has been dynamically added
  isDynamicAddedRoute: boolean;
  // To trigger a menu update
  lastBuildMenuTime: number;
  // 菜单列表
  frontMenuList: Menu[];
}
export const usePermissionStore = defineStore({
  id: "app-permission",
  state: (): PermissionState => ({
    // Whether the route has been dynamically added
    isDynamicAddedRoute: false,
    // To trigger a menu update
    lastBuildMenuTime: 0,
    // menu List
    // 菜单列表
    frontMenuList: [],
  }),
  getters: {
    getFrontMenuList(): Menu[] {
      return this.frontMenuList;
    },
    getLastBuildMenuTime(): number {
      return this.lastBuildMenuTime;
    },
    getIsDynamicAddedRoute(): boolean {
      return this.isDynamicAddedRoute;
    },
  },
  actions: {
    setFrontMenuList(list: Menu[]) {
      this.frontMenuList = list;
    },

    setLastBuildMenuTime() {
      this.lastBuildMenuTime = new Date().getTime();
    },

    setDynamicAddedRoute(added: boolean) {
      this.isDynamicAddedRoute = added;
    },
    resetState(): void {
      this.isDynamicAddedRoute = false;
      this.lastBuildMenuTime = 0;
    },
    async buildRoutesAction(): Promise<AppRouteRecordRaw[]> {
      const userStore = useUserStore();
      let routes: AppRouteRecordRaw[] = [];

      const routeRemoveIgnoreFilter = (route: AppRouteRecordRaw) => {
        const { meta } = route;
        const { ignoreRoute } = meta || {};
        return !ignoreRoute;
      };

      // 路由过滤器 在 函数filter 作为回调传入遍历使用
      const routeFilter = (route: AppRouteRecordRaw) => {
        if (route.meta?.ignoreAuth) return true;

        const roles: ManagerType[] = Array.isArray(route.meta?.roles)
          ? route.meta.roles
          : route.meta?.roles
            ? [route.meta.roles]
            : [];
        if (!roles.length) return true;

        if (
          userStore.isRoot &&
          roles.filter(
            (r) =>
              r == ManagerType.Root ||
              r == ManagerType.ProjectAdmin ||
              r == ManagerType.OrganizationAdmin ||
              r == ManagerType.OrganizationReviewer,
          ).length
        ) {
          return true;
        }
        if (
          userStore.isProjectAdmin &&
          roles.filter(
            (r) =>
              r == ManagerType.ProjectAdmin ||
              r == ManagerType.OrganizationAdmin ||
              r == ManagerType.OrganizationReviewer,
          ).length
        ) {
          return true;
        }
        if (
          userStore.isOrganizationAdmin &&
          roles.filter(
            (r) =>
              r == ManagerType.OrganizationAdmin ||
              r == ManagerType.OrganizationReviewer,
          ).length
        ) {
          return true;
        }
        if (
          userStore.isOrganizationReviewer &&
          roles.filter((r) => r == ManagerType.OrganizationReviewer).length
        ) {
          return true;
        }
        if (
          userStore.getUserInfo?.type == AccountType.Normal &&
          roles.filter((r) => r == ManagerType.Normal).length
        ) {
          return true;
        }
        if (userStore.getUserInfo?.type == AccountType.Normal) {
          const registerStore = useRegisterStore();
          if (
            !!registerStore.currentRegister?.unitRealname &&
            roles.filter((r) => r == ManagerType.Unit).length
          ) {
            return true;
          }
          if (
            !!registerStore.currentRegister?.playerRealname &&
            roles.filter((r) => r == ManagerType.Single).length
          ) {
            return true;
          }
        }
        return false;
      };

      /**
       * @description 根据设置的首页path，修正routes中的affix标记（固定首页）
       * */
      const patchHomeAffix = (routes: AppRouteRecordRaw[]) => {
        if (!routes || routes.length === 0) return;
        let homePath: string = userStore.getHomePage;

        function patcher(routes: AppRouteRecordRaw[], parentPath = "") {
          if (parentPath) parentPath = parentPath + "/";
          routes.forEach((route: AppRouteRecordRaw) => {
            const { path, children, redirect } = route;
            const currentPath = path.startsWith("/") ? path : parentPath + path;
            if (currentPath === homePath) {
              if (redirect) {
                homePath = route.redirect! as string;
              } else {
                route.meta = Object.assign({}, route.meta, { affix: true });
                throw new Error("end");
              }
            }
            children && children.length > 0 && patcher(children, currentPath);
          });
        }

        try {
          patcher(routes);
        } catch {
          // 已处理完毕跳出循环
        }
        return;
      };

      routes = filter(asyncRoutes, routeFilter) as AppRouteRecordRaw[];
      // 对一级路由再次根据角色权限过滤
      routes = routes.filter(routeFilter);
      // 将路由转换成菜单
      const menuList = transformRouteToMenu(routes, true);
      // 移除掉 ignoreRoute: true 的路由 非一级路由
      routes = filter(routes, routeRemoveIgnoreFilter);
      // 移除掉 ignoreRoute: true 的路由 一级路由；
      routes = routes.filter(routeRemoveIgnoreFilter);
      // 对菜单进行排序
      menuList.sort((a, b) => {
        return (Number(a.meta?.orderNo) || 0) - (Number(b.meta?.orderNo) || 0);
      });

      // 设置菜单列表
      this.setFrontMenuList(menuList);

      // Convert multi-level routing to level 2 routing
      // 将多级路由转换为 2 级路由
      routes = flatMultiLevelRoutes(routes);

      patchHomeAffix(routes);

      return routes;
    },
    async rebuildRoutesAction() {
      this.resetState();
      const routes = await this.buildRoutesAction();
      routes.forEach((route) => {
        router.addRoute(route as unknown as RouteRecordRaw);
      });
      router.addRoute(PAGE_NOT_FOUND_ROUTE as unknown as RouteRecordRaw);
      this.setDynamicAddedRoute(true);
    },
  },
});

// Need to be used outside the setup
export function usePermissionStoreWithOut() {
  return usePermissionStore(store);
}
